import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import "./i18n";
import { globalErrorHandler, globalRejectionHandler, startMonitoring } from "./lib/monitoring";

// Global error tracking
window.addEventListener("error", globalErrorHandler);
window.addEventListener("unhandledrejection", globalRejectionHandler);

// Start persistent monitoring (periodic flush to backend)
startMonitoring();

// Guard: skip SW in iframe/preview contexts
const isInIframe = (() => {
  try { return window.self !== window.top; } catch { return true; }
})();
const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((regs) =>
    regs.forEach((r) => r.unregister())
  );
} else if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((registration) => {
      let waitingWorker: ServiceWorker | null = registration.waiting ?? null;

      const announce = (worker: ServiceWorker) => {
        waitingWorker = worker;
        window.dispatchEvent(new Event("sw-update-available"));
      };

      // Nova versão já estava aguardando (aba reaberta)
      if (registration.waiting && navigator.serviceWorker.controller) {
        announce(registration.waiting);
      }

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // Não recarrega à força: avisa o usuário (UpdatePrompt).
            announce(newWorker);
          }
        });
      });

      // O usuário clicou em "Atualizar"
      window.addEventListener("sw-apply-update", () => {
        (waitingWorker ?? registration.waiting)?.postMessage({ type: "SKIP_WAITING" });
      });

      // Recarrega uma única vez quando o novo SW assume o controle
      let reloading = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });

      // Checagem agressiva: ao voltar o foco/aba e a cada 5 minutos
      const checkForUpdate = () => registration.update().catch(() => undefined);
      checkForUpdate();
      setInterval(checkForUpdate, 5 * 60 * 1000);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checkForUpdate();
      });
      window.addEventListener("focus", checkForUpdate);
      window.addEventListener("online", checkForUpdate);
    }).catch((err) => {
      console.warn("[SW] Registration failed:", err);
    });
  });
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
